/**
 * Form09_PatientHistoryForm.jsx
 * Collection: patient_history_forms
 * Redirect: /forms/10-patient-registration
 * Nearly identical scope to Form08 but shorter (5 medications, no OB/GYN detail).
 */
import React, { useRef, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/patient-history";
const NEXT    = "/forms/10-patient-registration";

const IMMUNIZATIONS = ["chickenpox","fluShot","gardasilHPV","hepatitisA","hepatitisB",
  "meningococcus","MMR","pneumonia","tdap","tetanus","zostavax"];
const IMM_LABELS = {
  chickenpox:"Chickenpox",fluShot:"Flu Shot",gardasilHPV:"Gardasil/HPV",
  hepatitisA:"Hepatitis A",hepatitisB:"Hepatitis B",meningococcus:"Meningococcus",
  MMR:"MMR",pneumonia:"Pneumonia",tdap:"Tdap",tetanus:"Tetanus",zostavax:"Zostavax (Shingles)",
};

const PMH_CONDITIONS = [
  ["anxietyDisorder","Anxiety Disorder"],["arthritis","Arthritis"],["asthma","Asthma"],
  ["bleedingDisorder","Bleeding Disorder"],["bloodClots","Blood Clots (DVT)"],["cancer","Cancer"],
  ["coronaryArteryDisease","Coronary Artery Disease"],["claustrophobic","Claustrophobic"],
  ["diabetesInsulin","Diabetes - Insulin"],["diabetesNonInsulin","Diabetes - Non-Insulin"],
  ["dialysis","Dialysis"],["diverticulitis","Diverticulitis"],["fibromyalgia","Fibromyalgia"],
  ["gout","Gout"],["pacemaker","Has Pacemaker"],["heartAttack","Heart Attack"],
  ["heartMurmur","Heart Murmur"],["hiatalHernia","Hiatal Hernia or Reflux"],
  ["hivAids","HIV or AIDS"],["highCholesterol","High Cholesterol"],["highBP","High Blood Pressure"],
  ["overactiveThyroid","Overactive Thyroid"],["kidneyDisease","Kidney Disease"],
  ["kidneyStones","Kidney Stones"],["legFootUlcers","Leg/Foot Ulcers"],
  ["liverDisease","Liver Disease"],["osteoporosis","Osteoporosis"],["polio","Polio"],
  ["pulmonaryEmbolism","Pulmonary Embolism"],["refluxUlcers","Reflux or Ulcers"],
  ["stroke","Stroke"],["tuberculosis","Tuberculosis"],["other","Other"],
];

const ROS_SYSTEMS = {
  allergicImmunologic:["Frequent Sneezing","Hives","Itching","Runny Nose","Sinus Pressure"],
  cardiovascular:["Arm Pain on Exertion","Chest Pain on Exertion","Chest Heaviness/Pressure",
    "Irregular Heart Beats (Palpitations)","Known Heart Murmur","Light-headed on Standing",
    "SOB When Lying Down","SOB When Walking","Swelling (Edema)"],
  constitutional:["Exercise Intolerance","Fatigue","Fever","Weight Gain","Weight Loss"],
  eyes:["Dry Eyes","Irritation","Vision Change"],
  earNoseMouthThroat:["Bleeding Gums","Difficulty Hearing","Dizziness","Dry Mouth","Ear Pain",
    "Frequent Infections","Frequent Nosebleeds","Hoarseness","Mouth Breathing","Mouth Ulcers",
    "Nose/Sinus Problems","Ringing in Ears"],
  endocrine:["Fatigue","Increased Thirst/Hunger/Urination"],
  gastrointestinal:["Abdominal Pain","Black/Tarry Stool","Blood in Stool","Change in Appetite",
    "Frequent Indigestion","Hemorrhoids","Trouble Swallowing","Vomiting","Vomiting Blood"],
  genitourinary:["Blood in Urine","Difficulty Urinating","Incomplete Emptying",
    "Increased Urinary Frequency","Urinary Loss of Control"],
  hematologicLymphatic:["Easy Bruising/Bleeding","Swollen Glands"],
  integumentary:["Changes in Moles","Dry Skin","Eczema","Growth/Lesions","Itching",
    "Jaundice (Yellow Skin/Eyes)","Rash"],
  musculoskeletal:["Back Pain","Joint Pain","Muscle Aches","Muscle Weakness"],
  neurological:["Dizziness","Fainting","Headaches","Memory Loss","Migraines","Numbness",
    "Restless Legs","Seizures","Weakness"],
  psychiatric:["Alcohol Overuse","Anxiety/Stress","Depression","Do Not Feel Safe in Relationship",
    "Mania","Sleep Problems"],
  respiratory:["Cough","Coughing Up Blood","Shortness of Breath","Sleep Apnea","Snoring","Wheezing"],
};

const initROS = () => Object.fromEntries(
  Object.entries(ROS_SYSTEMS).map(([sys,items])=>[sys, Object.fromEntries(items.map(i=>[i,false]))])
);

export default function PatientHistoryForm({ patientName="", pharmacyName="", pharmacyAddress="", onSubmit }) {
  const history = useHistory();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [form, setForm] = useState({
    mainReason:"", otherConcerns:"",
    allergies: [{allergy:"",reaction:""},{allergy:"",reaction:""},{allergy:"",reaction:""}],
    medications: Array(7).fill(null).map(()=>({drugName:"",strength:"",frequency:""})),
    immunizations: Object.fromEntries(IMMUNIZATIONS.map(k=>[k,{checked:false,date:""}])),

    // OB/GYN simplified
    lastPapDate:"", papAbnormal:false, lastMammogramDate:"", mammogramAbnormal:false,
    ageFirstMenstrual:"", lastMenstrual:"",
    numPregnancies:"", numBirths:"", numMiscarriages:"", numAbortions:"",
    cesarean:false, numCesarean:"",
    obgynSymptoms: Object.fromEntries([
      "bleedingBetweenPeriods","heavyPeriods","extremeMenstrualPain","vaginalDischarge",
      "nightBathroom","hotFlashes","breastLump","painfulIntercourse","sexuallyActive",
    ].map(k=>[k,false])),
    sexPartner:"", usesCondoms:"", birthControl:"", stiScreening:false,

    pmh: Object.fromEntries(PMH_CONDITIONS.map(([k])=>[k,false])),
    surgeries: Array(4).fill(null).map(()=>({surgery:"",reason:"",year:"",hospital:""})),
    familyHistory: Object.fromEntries(
      ["grandmotherMaternal","grandfatherMaternal","grandmotherPaternal","grandfatherPaternal",
       "father","mother","brotherSister","other"].map(rel=>([rel,{
        alive:"",age:"",
        conditions: Object.fromEntries(["alcoholism","arthritis","depression","cancer","diabetes",
          "geneticDisease","heartDisease","hypertension","osteoporosis","stroke"].map(k=>[k,false])),
      }]))
    ),
    social: {
      occupation:"", education:"", maritalStatus:"", exerciseLevel:"",
      caffeine:"", caffeinePerDay:"",
      drinksAlcohol:"", alcoholFrequency:"", drinksPerWeek:"",
      usesTobacco:"", everUsedTobacco:"",
      cigarettesPerDay:"", chewPerDay:"", cigarsPerDay:"", yearsUsed:"", yearQuit:"",
      drugs:"", drugsList:"",
    },
    ros: initROS(),
    weightGainLbs:"", weightLossLbs:"", eyeLastExamDate:"",
    additionalNotes:"",
    responsibleParty:patientName, relationship:"Self",
    sigDate: new Date().toISOString().split("T")[0],
  });

  const set = useCallback((k,v)=>setForm(f=>({...f,[k]:v})),[]);
  const setAllergyF = (i,f2,v)=>setForm(f=>{const a=[...f.allergies];a[i]={...a[i],[f2]:v};return{...f,allergies:a};});
  const setMedF     = (i,f2,v)=>setForm(f=>{const m=[...f.medications];m[i]={...m[i],[f2]:v};return{...f,medications:m};});
  const setSurgF    = (i,f2,v)=>setForm(f=>{const s=[...f.surgeries];s[i]={...s[i],[f2]:v};return{...f,surgeries:s};});
  const setImmF     = (k,f2,v)=>setForm(f=>({...f,immunizations:{...f.immunizations,[k]:{...f.immunizations[k],[f2]:v}}}));
  const setPmh      = (k,v)=>setForm(f=>({...f,pmh:{...f.pmh,[k]:v}}));
  const setRos      = (sys,item,v)=>setForm(f=>({...f,ros:{...f.ros,[sys]:{...f.ros[sys],[item]:v}}}));
  const setFamF     = (rel,k,v)=>setForm(f=>({...f,familyHistory:{...f.familyHistory,[rel]:{...f.familyHistory[rel],[k]:v}}}));
  const setFamCond  = (rel,k,v)=>setForm(f=>({...f,familyHistory:{...f.familyHistory,[rel]:{...f.familyHistory[rel],conditions:{...f.familyHistory[rel].conditions,[k]:v}}}}));
  const setSocial   = (k,v)=>setForm(f=>({...f,social:{...f.social,[k]:v}}));
  const setObgynSym = (k,v)=>setForm(f=>({...f,obgynSymptoms:{...f.obgynSymptoms,[k]:v}}));

  const getSig = ()=>(!sigRef.current||sigRef.current.isEmpty())?null:sigRef.current.toDataURL("image/png");

  const buildDoc = ()=>({
    formType:"patient_history_form",
    patient:{name:patientName},
    pharmacy:{name:pharmacyName, address:pharmacyAddress},
    mainReason:form.mainReason, otherConcerns:form.otherConcerns,
    allergies:form.allergies.filter(a=>a.allergy),
    medications:form.medications.filter(m=>m.drugName),
    immunizations:form.immunizations,
    obgyn:{
      lastPapDate:form.lastPapDate, papAbnormal:form.papAbnormal,
      lastMammogramDate:form.lastMammogramDate, mammogramAbnormal:form.mammogramAbnormal,
      ageFirstMenstrual:form.ageFirstMenstrual, lastMenstrual:form.lastMenstrual,
      numPregnancies:form.numPregnancies, numBirths:form.numBirths,
      numMiscarriages:form.numMiscarriages, numAbortions:form.numAbortions,
      cesarean:form.cesarean, numCesarean:form.numCesarean,
      symptoms:form.obgynSymptoms, sexPartner:form.sexPartner,
      usesCondoms:form.usesCondoms, birthControl:form.birthControl, stiScreening:form.stiScreening,
    },
    pastMedicalHistory:form.pmh,
    surgeries:form.surgeries.filter(s=>s.surgery),
    familyHistory:form.familyHistory,
    socialHistory:form.social,
    reviewOfSystems:form.ros,
    weightGainLbs:form.weightGainLbs, weightLossLbs:form.weightLossLbs,
    eyeLastExamDate:form.eyeLastExamDate,
    additionalNotes:form.additionalNotes,
    authorization:{
      responsibleParty:form.responsibleParty, relationship:form.relationship,
      sigDate:form.sigDate||null, signatureDataURL:getSig(),
    },
    meta:{submittedAt:new Date().toISOString(), formVersion:"1.0.0"},
  });

  const handleSubmit = async ()=>{
    if(sigEmpty){showToast("Please sign before submitting.","error");return;}
    setSubmitting(true);
    try{
      const doc=buildDoc();
      const res=typeof onSubmit==="function"?await onSubmit(doc):await saveToMongo(doc,API_URL);
      if(res?._id)setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    }catch(e){console.error(e);showToast("Save failed.","error");}
    finally{setSubmitting(false);}
  };

  const handleDownload = async ()=>{
    try{await downloadPDF(formRef,`patient-history-${patientName||"patient"}.pdf`);showToast("Downloaded!");}
    catch{showToast("PDF failed.","error");}
  };

  const ST = ({num,label})=><div className="wf-section-title"><span className="num">{num}</span>{label}</div>;

  return (
    <FormShell title="Patient History Form"
      subtitle="All questions are optional and strictly confidential. Please answer to the best of your ability."
      dbLabel="MongoDB · patient_history_forms"
      patientName={patientName} savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <ST num="1" label="Reason for Visit" />
        <div className="wf-grid" style={{gap:14}}>
          <Field label="Main reason for today's visit">
            <textarea value={form.mainReason} onChange={e=>set("mainReason",e.target.value)} placeholder="Describe your main concern…" />
          </Field>
          <Field label="Other concerns">
            <textarea value={form.otherConcerns} onChange={e=>set("otherConcerns",e.target.value)} placeholder="Any other concerns…" />
          </Field>
        </div>
      </div>

      <div className="wf-section">
        <ST num="2" label="Allergies" />
        {form.allergies.map((a,i)=>(
          <div key={i} className="wf-grid wf-grid-2" style={{marginBottom:10}}>
            <Field label={`Allergy ${i+1}`}><input type="text" value={a.allergy} onChange={e=>setAllergyF(i,"allergy",e.target.value)} placeholder="Allergy" /></Field>
            <Field label="Reaction"><input type="text" value={a.reaction} onChange={e=>setAllergyF(i,"reaction",e.target.value)} placeholder="Reaction" /></Field>
          </div>
        ))}
      </div>

      <div className="wf-section">
        <ST num="3" label="Medications" />
        {form.medications.map((m,i)=>(
          <div key={i} className="wf-grid wf-grid-3" style={{marginBottom:10}}>
            <Field label={`Drug ${i+1}`}><input type="text" value={m.drugName} onChange={e=>setMedF(i,"drugName",e.target.value)} placeholder="Drug name" /></Field>
            <Field label="Strength"><input type="text" value={m.strength} onChange={e=>setMedF(i,"strength",e.target.value)} placeholder="Strength" /></Field>
            <Field label="Frequency"><input type="text" value={m.frequency} onChange={e=>setMedF(i,"frequency",e.target.value)} placeholder="Frequency" /></Field>
          </div>
        ))}
      </div>

      <div className="wf-section">
        <ST num="4" label="Immunization History" />
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {IMMUNIZATIONS.map(k=>(
            <div key={k} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",border:`1.5px solid ${C.border}`,borderRadius:8,background:form.immunizations[k].checked?C.tealLt:C.white}}>
              <input type="checkbox" style={{accentColor:C.teal}} checked={form.immunizations[k].checked} onChange={e=>setImmF(k,"checked",e.target.checked)} />
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600}}>{IMM_LABELS[k]}</div>
                {form.immunizations[k].checked&&<input type="date" value={form.immunizations[k].date} onChange={e=>setImmF(k,"date",e.target.value)} style={{marginTop:4,fontSize:12,padding:"4px 8px",border:`1px solid ${C.border}`,borderRadius:6,width:"100%"}} />}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="wf-section">
        <ST num="5" label="OB/GYN History (Women Only)" />
        <div className="wf-grid wf-grid-2" style={{marginBottom:14}}>
          <Field label="Last PAP Date"><input type="date" value={form.lastPapDate} onChange={e=>set("lastPapDate",e.target.value)} /></Field>
          <Field label="PAP Abnormal?"><label className="wf-check-label" style={{display:"inline-flex"}}><input type="checkbox" checked={form.papAbnormal} onChange={e=>set("papAbnormal",e.target.checked)} />Abnormal</label></Field>
          <Field label="Last Mammogram Date"><input type="date" value={form.lastMammogramDate} onChange={e=>set("lastMammogramDate",e.target.value)} /></Field>
          <Field label="Mammogram Abnormal?"><label className="wf-check-label" style={{display:"inline-flex"}}><input type="checkbox" checked={form.mammogramAbnormal} onChange={e=>set("mammogramAbnormal",e.target.checked)} />Abnormal</label></Field>
          {[["ageFirstMenstrual","Age of first period"],["lastMenstrual","Last period / menopause age"],
            ["numPregnancies","Pregnancies"],["numBirths","Births"],["numMiscarriages","Miscarriages"],["numAbortions","Abortions"]].map(([k,lbl])=>(
            <Field key={k} label={lbl}><input type="text" value={form[k]} onChange={e=>set(k,e.target.value)} /></Field>
          ))}
        </div>
        <div className="wf-check-group">
          {[["bleedingBetweenPeriods","Bleeding between periods"],["heavyPeriods","Heavy periods"],
            ["extremeMenstrualPain","Extreme menstrual pain"],["vaginalDischarge","Vaginal itching/burning/discharge"],
            ["nightBathroom","Wake at night to use bathroom"],["hotFlashes","Hot flashes"],
            ["breastLump","Breast lump/nipple discharge"],["painfulIntercourse","Painful intercourse"],
            ["sexuallyActive","Sexually active"],["stiScreening","Interested in STI screening"]].map(([k,lbl])=>(
            <label className="wf-check-label" key={k}>
              <input type="checkbox" checked={!!form.obgynSymptoms[k]||form[k]||false}
                onChange={e=>k==="stiScreening"?set("stiScreening",e.target.checked):setObgynSym(k,e.target.checked)} />{lbl}
            </label>
          ))}
        </div>
      </div>

      <div className="wf-section">
        <ST num="6" label="Past Medical History" />
        <div className="wf-check-group">
          {PMH_CONDITIONS.map(([k,lbl])=>(
            <label className="wf-check-label" key={k}><input type="checkbox" checked={!!form.pmh[k]} onChange={e=>setPmh(k,e.target.checked)} />{lbl}</label>
          ))}
        </div>
      </div>

      <div className="wf-section">
        <ST num="7" label="Past Surgical History" />
        {form.surgeries.map((s,i)=>(
          <div key={i} className="wf-grid wf-grid-4" style={{marginBottom:10}}>
            <Field label={`Surgery ${i+1}`}><input type="text" value={s.surgery} onChange={e=>setSurgF(i,"surgery",e.target.value)} placeholder="Procedure" /></Field>
            <Field label="Reason"><input type="text" value={s.reason} onChange={e=>setSurgF(i,"reason",e.target.value)} placeholder="Reason" /></Field>
            <Field label="Year"><input type="number" value={s.year} onChange={e=>setSurgF(i,"year",e.target.value)} /></Field>
            <Field label="Hospital"><input type="text" value={s.hospital} onChange={e=>setSurgF(i,"hospital",e.target.value)} placeholder="Hospital" /></Field>
          </div>
        ))}
      </div>

      <div className="wf-section">
        <ST num="8" label="Family Health History" />
        {Object.entries(form.familyHistory).map(([rel,data])=>{
          const lbl=rel.replace(/([A-Z])/g," $1").replace(/^./,s=>s.toUpperCase());
          return (
            <div key={rel} style={{marginBottom:12,padding:"12px 14px",border:`1px solid ${C.border}`,borderRadius:10}}>
              <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>{lbl}</div>
              <div className="wf-grid wf-grid-2" style={{marginBottom:8}}>
                <Field label="Alive?">
                  <div className="wf-radio-group">
                    {["Y","N"].map(o=><label className="wf-radio-label" key={o}><input type="radio" name={`${rel}_a`} value={o} checked={data.alive===o} onChange={e=>setFamF(rel,"alive",e.target.value)} />{o==="Y"?"Yes":"No"}</label>)}
                  </div>
                </Field>
                <Field label="Age"><input type="number" value={data.age} onChange={e=>setFamF(rel,"age",e.target.value)} /></Field>
              </div>
              <div className="wf-check-group">
                {Object.entries(data.conditions).map(([k,v])=>{
                  const cl=k.replace(/([A-Z])/g," $1").replace(/^./,s=>s.toUpperCase());
                  return <label className="wf-check-label" key={k}><input type="checkbox" checked={v} onChange={e=>setFamCond(rel,k,e.target.checked)} />{cl}</label>;
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="wf-section">
        <ST num="9" label="Social History" />
        <div className="wf-grid wf-grid-2" style={{marginBottom:14}}>
          <Field label="Occupation"><input type="text" value={form.social.occupation} onChange={e=>setSocial("occupation",e.target.value)} /></Field>
          <Field label="Education">
            <div className="wf-radio-group">
              {["Less than 8th grade","High school","2 year college","4 year college","Post graduate"].map(o=>(
                <label className="wf-radio-label" key={o}><input type="radio" name="edu" value={o} checked={form.social.education===o} onChange={e=>setSocial("education",e.target.value)} />{o}</label>
              ))}
            </div>
          </Field>
          <Field label="Marital Status">
            <div className="wf-radio-group">
              {["Married","Single","Divorced","Separated","Widowed","Domestic partner"].map(o=>(
                <label className="wf-radio-label" key={o}><input type="radio" name="marital" value={o} checked={form.social.maritalStatus===o} onChange={e=>setSocial("maritalStatus",e.target.value)} />{o}</label>
              ))}
            </div>
          </Field>
          <Field label="Exercise Level">
            <div className="wf-radio-group">
              {["None","Occasional","Moderate","High level"].map(o=>(
                <label className="wf-radio-label" key={o}><input type="radio" name="exlvl" value={o} checked={form.social.exerciseLevel===o} onChange={e=>setSocial("exerciseLevel",e.target.value)} />{o}</label>
              ))}
            </div>
          </Field>
          <Field label="Drinks Alcohol?">
            <div className="wf-radio-group">
              {["Yes","No"].map(o=><label className="wf-radio-label" key={o}><input type="radio" name="alc" value={o} checked={form.social.drinksAlcohol===o} onChange={e=>setSocial("drinksAlcohol",e.target.value)} />{o}</label>)}
            </div>
          </Field>
          <Field label="Uses Tobacco?">
            <div className="wf-radio-group">
              {["Yes","No"].map(o=><label className="wf-radio-label" key={o}><input type="radio" name="tob" value={o} checked={form.social.usesTobacco===o} onChange={e=>setSocial("usesTobacco",e.target.value)} />{o}</label>)}
            </div>
          </Field>
          <Field label="Recreational Drugs?">
            <div className="wf-radio-group">
              {["Yes","No"].map(o=><label className="wf-radio-label" key={o}><input type="radio" name="drugs" value={o} checked={form.social.drugs===o} onChange={e=>setSocial("drugs",e.target.value)} />{o}</label>)}
            </div>
          </Field>
        </div>
        {form.social.drugs==="Yes"&&<Field label="List drugs"><textarea value={form.social.drugsList} onChange={e=>setSocial("drugsList",e.target.value)} style={{minHeight:60}} /></Field>}
      </div>

      <div className="wf-section">
        <ST num="10" label="Review of Systems" />
        {Object.entries(ROS_SYSTEMS).map(([sys,items])=>(
          <div key={sys} style={{marginBottom:14}}>
            <div style={{fontSize:12,fontWeight:700,color:C.navy,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>
              {sys.replace(/([A-Z])/g," $1").replace(/^./,s=>s.toUpperCase())}
            </div>
            <div className="wf-check-group">
              {items.map(item=>(
                <label className="wf-check-label" key={item}>
                  <input type="checkbox" checked={!!form.ros[sys][item]} onChange={e=>setRos(sys,item,e.target.checked)} />{item}
                </label>
              ))}
            </div>
          </div>
        ))}
        <div className="wf-grid wf-grid-3" style={{marginTop:10}}>
          <Field label="Weight Gain (lbs)"><input type="number" value={form.weightGainLbs} onChange={e=>set("weightGainLbs",e.target.value)} /></Field>
          <Field label="Weight Loss (lbs)"><input type="number" value={form.weightLossLbs} onChange={e=>set("weightLossLbs",e.target.value)} /></Field>
          <Field label="Last Eye Exam"><input type="date" value={form.eyeLastExamDate} onChange={e=>set("eyeLastExamDate",e.target.value)} /></Field>
        </div>
      </div>

      <div className="wf-section">
        <ST num="11" label="Additional Notes" />
        <Field label="Any other information for your provider">
          <textarea value={form.additionalNotes} onChange={e=>set("additionalNotes",e.target.value)} style={{minHeight:80}} />
        </Field>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const PatientHistorySchema = new Schema({
  formType: { type:String, default:"patient_history_form" },
  patient: { name:String },
  pharmacy: { name:String, address:String },
  mainReason:String, otherConcerns:String,
  allergies:   [{ allergy:String, reaction:String }],
  medications: [{ drugName:String, strength:String, frequency:String }],
  immunizations: Schema.Types.Mixed,
  obgyn: Schema.Types.Mixed,
  pastMedicalHistory: Schema.Types.Mixed,
  surgeries: [{ surgery:String, reason:String, year:Number, hospital:String }],
  familyHistory: Schema.Types.Mixed,
  socialHistory: Schema.Types.Mixed,
  reviewOfSystems: Schema.Types.Mixed,
  weightGainLbs:String, weightLossLbs:String, eyeLastExamDate:String,
  additionalNotes:String,
  authorization: { responsibleParty:String, relationship:String, sigDate:Date, signatureDataURL:String },
  meta: { submittedAt:Date, formVersion:String },
}, { timestamps:true, collection:"patient_history_forms" });
*/
